import { Stack, Box, Typography, Chip } from "@mui/material";
import type { DocumentShowQueryQueryType } from "@/types/graphql/admin.generated";
import type { DocumentVersionType } from "@/types/graphql/admin.generated";
import { DocumentVersionType as DocumentVersionEnum } from "@/admin/entities/documents";
import { CustomCellDate } from "@/shared/components";

interface DocumentShowProps {
  document?: DocumentShowQueryQueryType["document"];
  editData?: unknown;
  id?: string;
}

/**
 * Компонент отображения деталей документа
 * Показывает информацию о документе и историю версий
 */
export function DocumentShow({
  document: documentProp,
  editData,
}: DocumentShowProps) {
  // Извлекаем document из editData если document не передан напрямую
  const document =
    documentProp ||
    (editData as DocumentShowQueryQueryType | undefined)?.document;
  if (!document) {
    return (
      <Box padding={3}>
        <Typography variant="h6" color="error">
          Документ не найден
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Typography variant="h5">{document.title}</Typography>

      <Box>
        <Typography variant="body2" color="text.secondary">
          Slug
        </Typography>
        <Typography variant="body1" fontFamily="monospace">
          {document.slug}
        </Typography>
      </Box>

      <Box>
        <Typography variant="body2" color="text.secondary">
          Тип
        </Typography>
        <Chip label={document.type} size="small" color="primary" />
      </Box>

      {document.platform && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Платформа
          </Typography>
          <Typography variant="body1">{document.platform.title}</Typography>
        </Box>
      )}

      {document.language && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Язык
          </Typography>
          <Typography variant="body1">{document.language.title}</Typography>
        </Box>
      )}

      <Box>
        <Typography variant="body2" color="text.secondary">
          Статус публикации
        </Typography>
        <Chip
          label={document.isPublished ? "Опубликовано" : "Черновик"}
          size="small"
          color={document.isPublished ? "success" : "default"}
        />
      </Box>

      <Box>
        <Typography variant="body2" color="text.secondary">
          Версия
        </Typography>
        <Chip
          label={document.version}
          size="small"
          color={
            document.version === DocumentVersionEnum.LatestType
              ? "primary"
              : "default"
          }
        />
      </Box>

      <Box>
        <Typography variant="body2" color="text.secondary">
          Дата вступления в силу
        </Typography>
        <CustomCellDate date={document.date} />
      </Box>

      {document.versions && document.versions.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            История версий ({document.versions.length})
          </Typography>
          <Stack spacing={1}>
            {document.versions.map(
              (version: {
                id: string;
                version: DocumentVersionType;
                date: any;
                createdAt: any;
              }) => (
                <Box
                  key={version.id}
                  sx={{
                    p: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" display="block">
                    Версия: {version.version}
                  </Typography>
                  <Typography variant="caption" display="block">
                    Дата: <CustomCellDate date={version.date} />
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    color="text.secondary"
                  >
                    Создана: <CustomCellDate date={version.createdAt} />
                  </Typography>
                </Box>
              )
            )}
          </Stack>
        </Box>
      )}

      <Typography variant="h6" mt={2}>
        Системная информация
      </Typography>

      <Box>
        <Typography variant="body2" color="text.secondary">
          ID
        </Typography>
        <Typography variant="body1" fontFamily="monospace">
          {document.id}
        </Typography>
      </Box>

      <Box>
        <Typography variant="body2" color="text.secondary">
          Дата создания
        </Typography>
        <CustomCellDate date={document.createdAt} />
      </Box>

      {document.updatedAt && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Дата обновления
          </Typography>
          <CustomCellDate date={document.updatedAt} />
        </Box>
      )}

      {document.deletedAt && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Дата удаления
          </Typography>
          <CustomCellDate date={document.deletedAt} />
        </Box>
      )}
    </Stack>
  );
}
